import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import {
  faPen,
  faUpRightAndDownLeftFromCenter,
} from '@fortawesome/pro-solid-svg-icons';
import type { JSONContent } from '@tiptap/react';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import { useController } from 'react-hook-form';
import {
  DocMemo,
  EMPTY_TIPTAP_DOCUMENT,
  getDocumentPreviewFromContent,
  getParsedDocument,
  isEmptyDocument,
} from 'models/Memo';
import { useDraft } from 'xstate/hooks/useDraft';
import { useEditor } from 'xstate/hooks/useEditor';
import { discardDraft } from 'xstate/actors/DraftActor';
import { openEditor } from 'xstate/actors/EditorActor';
import { preloadDocEditor } from 'elements/DocEditor';
import { ClosingBehaviour, EditorMode } from 'xstate/machines/EditorMachine';
import { useMemoFormContext } from '../context/FormContext';
import TitleField from '../fields/TitleField';
import { DiscardDraftBanner } from '../banners/DiscardDraftBanner';

interface Props {
  memo?: DocMemo;
}

// Doc memos aren't written inside the composer. The form shows a card that
// opens the full-page editor; what the user writes there comes back through
// the draft machine and becomes the form's `document` value. Before anything
// is written the card is a "Compose" call to action; afterwards it is a
// one-line preview with a word count that reopens the editor. Same as the
// macOS and iOS composers, and it keeps the editor code out of the dashboard
// until someone actually writes a doc.
const DocForm: React.FC<Props> = ({ memo }) => {
  const t = useTranslations();
  const { isOpen } = useEditor();
  const { currentDraft, hasDraftContent, hasDraftChanged } = useDraft(memo);
  const { isTitleVisible, control, setValue, resetField } =
    useMemoFormContext();

  // The document lives in the form so submission and validation see it, but
  // nothing in the form edits it directly: the editor writes the draft and
  // the effect below copies the draft in.
  const { field } = useController({
    control,
    name: 'document',
    rules: {
      validate: (value: JSONContent) =>
        !isEmptyDocument(value) || t('memoComposer.contentRequired'),
    },
  });

  // When the editor closes, the form takes what the draft now holds: what
  // was written, or, after a discard, nothing for a new memo and the saved
  // document for an existing one. Only runs on an open → closed change, not
  // on mount, so the form's defaults are left alone.
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    const closedJustNow = wasOpen.current && !isOpen;
    wasOpen.current = isOpen;
    if (!closedJustNow) return;

    const document = hasDraftContent
      ? currentDraft.document
      : memo
        ? getParsedDocument(memo)
        : EMPTY_TIPTAP_DOCUMENT;
    setValue('document', document, { shouldValidate: true, shouldDirty: true });
  }, [isOpen]);

  const preview = useMemo(
    () => getDocumentPreviewFromContent(field.value),
    [field.value],
  );
  const isComposed = !isEmptyDocument(field.value);

  const onOpenEditorClick = () => {
    openEditor(memo?.id, EditorMode.Edit, ClosingBehaviour.NoAction, false);
  };

  // The editor is a separate chunk. Start loading it when the user shows
  // interest in the card so opening it feels instant.
  const warmEditor = preloadDocEditor;

  const onDiscardDraftClick = () => {
    const originalDocument = memo
      ? getParsedDocument(memo)
      : EMPTY_TIPTAP_DOCUMENT;
    discardDraft(memo?.id);
    resetField('document', {
      defaultValue: originalDocument,
      keepDirty: false,
    });
  };

  return (
    <>
      {isTitleVisible && <TitleField />}

      {memo && hasDraftChanged && (
        <DiscardDraftBanner onDiscard={onDiscardDraftClick} />
      )}

      {isComposed ? (
        <button
          type="button"
          onClick={onOpenEditorClick}
          onMouseEnter={warmEditor}
          onFocus={warmEditor}
          className={styles.composed}
          aria-label={t('memoComposer.doc.editDocument')}
        >
          <FontAwesomeIcon
            icon={faFileLines}
            className={styles.icon}
            aria-hidden="true"
          />
          <span className={styles.text}>
            {preview.firstLine && (
              <span className={styles.firstLine}>{preview.firstLine}</span>
            )}
            <span className={styles.subtitle}>
              {t('memoCard.doc.words', { count: preview.words })}
            </span>
          </span>
          <FontAwesomeIcon
            icon={faPen}
            className={styles.action}
            aria-hidden="true"
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpenEditorClick}
          onMouseEnter={warmEditor}
          onFocus={warmEditor}
          className={styles.empty}
        >
          <FontAwesomeIcon
            icon={faFileLines}
            className={styles.icon}
            aria-hidden="true"
          />
          <span className={styles.text}>
            <span className={styles.title}>
              {t('memoComposer.doc.composeTitle')}
            </span>
            <span className={styles.subtitle}>
              {t('memoComposer.doc.composeSubtitle')}
            </span>
          </span>
          <FontAwesomeIcon
            icon={faUpRightAndDownLeftFromCenter}
            className={styles.action}
            aria-hidden="true"
          />
        </button>
      )}
    </>
  );
};

export default DocForm;

const card = 'flex w-full items-center gap-3 text-left focus:outline-none';

const styles = {
  empty: classNames(
    card,
    'rounded-2xl border-2 border-dashed border-stroke p-4 transition-colors hover:bg-surface-field',
  ),
  composed: classNames(
    card,
    'rounded-2xl bg-surface-field p-3 transition-colors hover:bg-ink-secondary/12',
  ),
  icon: 'text-xl text-accent-ink',
  text: 'flex min-w-0 flex-1 flex-col gap-0.5',
  title: 'text-base font-semibold text-ink-primary',
  firstLine: 'truncate text-sm text-ink-primary',
  subtitle: 'text-sm text-ink-secondary',
  action: 'text-xs text-ink-secondary',
};
